import { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import Dialog from 'src/components/shared/ui/Dialog/Dialog'
import { SearchIcon } from 'src/components/shared/ui/Icons'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import { SwitchField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import InputField from 'src/components/shared/ui/form/fields/InputField'
import LexnetIconButton from 'src/components/shared/ui/form/fields/LexnetIconButton'
import DestinoForm from 'src/components/views/EscritosLexnet/components/Tramite/components/Destino/components/forms/DestinoForm'
import JuzgadoGuardiaSwitch from 'src/components/views/EscritosLexnet/components/shared/components/OrganoDestino/JuzgadoGuardiaSwitch'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import useClearFormSections from 'src/components/views/EscritosLexnet/components/shared/hooks/useClearFormSections'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import {
  OrganoDestinoFormInitialvalues,
  OrganoDestinoFormValidations,
  OrganoDestinoFormValues
} from 'src/components/views/EscritosLexnet/components/shared/types/organoDestinoTypes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { LiteralTiposOrden, TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import useNewModal from 'src/hooks/useModal'

const styles = {
  '& .MuiInputBase-input, & .MuiInputBase-root': {
    cursor: 'pointer'
  }
}

const infoBarStyles = {
  marginBottom: 2
}

function OrganoDestino(): JSX.Element {
  const [disabledRemoveButton, setDisabledRemoveButton] = useState(true)
  const { values, setFieldValue } = useFormikContext<EscritosFormValues>()
  const {
    isOpened: isOrganoModalOpened,
    close: closeOrganoModal,
    open: openOrganoModal
  } = useNewModal()
  const orden = values[FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION]
  const organoDestino = values[FormField.ORGANO_DESTINO]
  const procedimiento = values[FormField.PROCEDIMIENTO]
  const intervinientes = values[FormField.INTERVINIENTES]
  const documentoPrincipal = values[FormField.DOCUMENTO_PRINCIPAL]
  const documentosAnexos = values[FormField.DOCUMENTOS_ANEXOS]

  const { tipoMensaje } = useTipoMensajeContext()

  const showJuzgadoGuardiaSwitch =
    values[FormField.ORGANO_DESTINO] !== undefined &&
    values[FormField.ORGANO_DESTINO] !== null &&
    values[FormField.ORGANO_DESTINO]?.organoDestinoOrganoJudicial !== null &&
    values[FormField.ORGANO_DESTINO]?.organoDestinoOrganoJudicial?.juzgadoGuardia &&
    (orden ?? '').toUpperCase() === LiteralTiposOrden.PENAL &&
    (tipoMensaje === TipoMensaje.ESCRITO || tipoMensaje === TipoMensaje.INICIADOR_ASUNTO)

  const {
    clearDatosSection,
    clearPartialAdjuntosSection,
    clearPartialIntervinientesSection,
    clearPartialProcedimientoSection,
    clearOrganoDestinoFieldset
  } = useClearFormSections()

  useEffect(() => {
    if (organoDestino) {
      if (tipoMensaje !== TipoMensaje.ESCRITO) {
        clearDatosSection(true)
      }

      if (documentoPrincipal[0].archivo || documentosAnexos.length) {
        clearPartialAdjuntosSection(values)
      }

      if (intervinientes?.length) {
        clearPartialIntervinientesSection(values)
      }

      if (procedimiento && tipoMensaje === TipoMensaje.ESCRITO) {
        clearPartialProcedimientoSection(values)
      }
    } else {
      setDisabledRemoveButton(true)
    }

    // clearProcedimientoFieldset()
    // clearNIGFieldset()

    // eslint-disable-next-line
  }, [organoDestino])

  const clearOrgano = useCallback(() => {
    clearOrganoDestinoFieldset()

    if (tipoMensaje === TipoMensaje.ESCRITO) {
      clearPartialProcedimientoSection(values)
    }

    if (tipoMensaje !== TipoMensaje.ESCRITO) {
      clearDatosSection(true)
    }

    clearPartialIntervinientesSection(values)
    clearPartialAdjuntosSection(values)

    setDisabledRemoveButton(true)

    // eslint-disable-next-line
  }, [values])

  const DeleteButton = useMemo((): JSX.Element => {
    // if (tipoMensaje !== TipoMensaje.INICIADOR_ASUNTO) {
    // setDisabledRemoveButton(true)
    return (
      <RemoveInfoButton
        disabled={disabledRemoveButton}
        clear={clearOrgano}
        firstDialogMessage={t('dialog.message.remove_organo_destino')}
      />
    )
    // }
    // return <></>
    // eslint-disable-next-line
  }, [tipoMensaje, disabledRemoveButton, clearOrgano])

  function getButtons() {
    return (
      <>
        <LexnetIconButton title={t('tooltips.buscar_organo_destino')} handleClick={openOrganoModal}>
          <SearchIcon />
        </LexnetIconButton>
        {DeleteButton}
      </>
    )
  }

  function formSubmit(values: OrganoDestinoFormValues) {
    setFieldValue(FormField.ORGANO_DESTINO, values, false)

    setFieldValue(
      FormField.ORGANO_DESTINO_DESCRIPCION,
      `${values[FormField.ORGANO_DESTINO_ORGANO_JUDICIAL]?.nombre} DE ${values[
        FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL
      ]?.nombreMunicipio.toUpperCase()}`,
      false
    )
    setFieldValue(
      FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION,
      values[FormField.ORGANO_DESTINO_ORDEN_JUDICIAL]?.descripcion
    )

    setDisabledRemoveButton(false)
    closeOrganoModal()
  }

  const showSwitchDeTituloJudicial = useCallback((): boolean => {
    const ordenes = [LiteralTiposOrden.CIVIL, LiteralTiposOrden.SOCIAL]

    return (
      ordenes.includes((orden ?? '').toUpperCase() as LiteralTiposOrden) &&
      tipoMensaje === TipoMensaje.INICIADOR_EJECUCION
    )
  }, [orden, tipoMensaje])

  useEffect(() => {
    if (showSwitchDeTituloJudicial()) {
      setFieldValue(FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL, true, false)
    } else {
      setFieldValue(FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL, null, false)
    }
  }, [orden, setFieldValue, showSwitchDeTituloJudicial])

  return (
    <>
      <FieldsetGroup
        legend={t('form.legend.organo_destino')}
        buttons={getButtons()}
        isSecondaryType
      >
        {organoDestino && !orden && (
          <Box sx={infoBarStyles}>
            <InfoBar
              message={t('form.validation.warning_organo_orden')}
              type={AlertType.WARNING}
              showCloseIcon={false}
            />
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} onClick={openOrganoModal}>
            <InputField
              id={FormField.ORGANO_DESTINO_DESCRIPCION}
              label={t('form.field.organo')}
              readOnly
              required
              sx={styles}
            />
          </Grid>
          <Grid item xs={6} sm={4} onClick={openOrganoModal}>
            <InputField
              id={FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION}
              label={t('form.field.orden')}
              readOnly
              required
              sx={styles}
            />
          </Grid>
        </Grid>
        {showSwitchDeTituloJudicial() && (
          <SwitchField
            id={FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL}
            label={t('form.field.de_titulo_judicial')}
          />
        )}
        <JuzgadoGuardiaSwitch showSwitch={showJuzgadoGuardiaSwitch} />
      </FieldsetGroup>
      <Dialog
        title={t('form.dialog.title.organo_destino')}
        isOpen={isOrganoModalOpened}
        close={closeOrganoModal}
        cancel={() => ''}
        withForm
        validateFormik={false}
        formInitialValues={values[FormField.ORGANO_DESTINO] || OrganoDestinoFormInitialvalues}
        formValidationScheme={OrganoDestinoFormValidations}
        formHandleSubmit={formSubmit}
      >
        <DestinoForm />
      </Dialog>
    </>
  )
}

export default OrganoDestino
